import React from 'react';
import { any, bool, func, oneOf, string, number } from 'prop-types';
import styled from 'styled-components';

const paddingSizesMap = {
  xl: '2rem 3rem',
  l: '1.6rem 3rem',
  m: '1.4rem 2rem',
  s: '0.8rem 1rem',
};

const fontSizesMap = (themeFontSizes) => ({
  xl: themeFontSizes.medium,
  l: themeFontSizes.normal,
  m: '1.4rem',
  s: themeFontSizes.small,
});

const BaseButton = styled.button`
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: all 0.2s;
  ${(props) => !!props.width && `width: ${props.width}px`};
  background-color: ${(props) => props.disabled && props.theme.colors.grey2} !important;

  :hover {
    cursor: ${(props) => (props.disabled ? 'normal' : 'pointer')};
  }

  :active {
    transform: scale(0.98);
  }
`;

const PrimaryButton = styled(BaseButton)`
  background-color: ${(props) => props.theme.colors.medium};
  color: ${(props) => props.theme.colors.white};
  font-weight: bold;
  font-size: ${(props) => fontSizesMap(props.theme.fontSizes)[props.size]};
  padding: ${(props) => paddingSizesMap[props.size]};
  border-radius: 6rem;

  :hover {
    background-color: ${(props) => props.theme.colors.light};
  }

  :active {
    background-color: ${(props) => props.theme.colors.dark};
  }
`;

const SecondaryButton = styled(BaseButton)`
  background-color: ${(props) => props.theme.colors.white};
  border: 2px solid ${(props) => props.theme.colors.medium};
  border-radius: 6rem;
  color: ${(props) => props.theme.colors.medium};
  font-weight: bold;
  font-size: ${(props) => fontSizesMap(props.theme.fontSizes)[props.size]};
  padding: ${(props) => paddingSizesMap[props.size]};

  :hover {
    color: ${(props) => props.theme.colors.light};
    border-color: ${(props) => props.theme.colors.light};
  }

  :active {
    color: ${(props) => props.theme.colors.dark};
    border-color: ${(props) => props.theme.colors.dark};
  }
`;

const RoundedButton = styled(BaseButton)`
  background-color: ${(props) => props.theme.colors.lightBlue};
  border-radius: 6rem;
  color: ${(props) => props.theme.colors.white};
  font-weight: 600;
  padding: 1rem 2rem;
  :hover {
    background-color: ${(props) => props.theme.colors.mediumBlue};
  }

  :active {
    background-color: ${(props) => props.theme.colors.darkBlue};
  }
`;

const types = ['primary', 'secondary', 'rounded'];

const Components = {
  primary: PrimaryButton,
  secondary: SecondaryButton,
  rounded: RoundedButton,
};

export const CustomButton = ({ handler, variant, size, disabled, className, children, width, ...rest }) => {
  const Button = Components[variant];
  return (
    <Button onClick={handler} size={size} disabled={disabled} width={width} className={className} {...rest}>
      {children}
    </Button>
  );
};

CustomButton.propTypes = {
  children: any.isRequired,
  handler: func,
  size: string,
  disabled: bool,
  variant: oneOf(types),
  className: string,
  width: number,
};

CustomButton.defaultProps = {
  size: 'm',
  disabled: false,
  variant: 'primary',
  className: '',
  handler: undefined,
  width: 0,
};
